import { c as _c } from "react/compiler-runtime";
import { jwtDecode } from "jwt-decode";
import { useMemo } from "react";
export type AuthHeaders = Record<string, string>;
interface WaveReadJwtPayload {
  readonly sub?: string | undefined;
  readonly role?: string | null | undefined;
  readonly exp?: number | undefined;
}
export interface WaveReadVerifiedIdentity {
  readonly addressKey: string;
  readonly activeProfileProxyId: string | null;
  readonly activeProfileProxyCreatorId: string | null;
  readonly identityKey: string;
  readonly jwtExpiresAt: number;
  readonly authHeaders: AuthHeaders;
}
interface WaveReadJwtIdentity {
  readonly addressKey: string;
  readonly proxyCreatorId: string | null;
  readonly jwtExpiresAt: number;
}
export interface WaveReadTemporaryProxyRoleIdentity {
  readonly addressKey: string;
  readonly proxyCreatorId: string;
  readonly identityKey: string;
}
export interface WaveReadIdentityConfig {
  readonly address: string | undefined;
  readonly activeProfileProxyId: string | null;
  readonly activeProfileProxyCreatorId: string | null;
  readonly walletAuth: string | null;
}
interface WaveReadIdentityState {
  readonly addressKey: string | null;
  readonly activeProfileProxyId: string | null;
  readonly identityKey: string;
  readonly proxyRoleIdentityKey: string | null;
  readonly temporaryProxyRoleIdentity: WaveReadTemporaryProxyRoleIdentity | undefined;
  readonly verifiedIdentity: WaveReadVerifiedIdentity | undefined;
}
const getAddressKey = (address: string | undefined): string | null => address?.toLowerCase() ?? null;
export const getWaveReadIdentityKey = ({
  addressKey,
  activeProfileProxyId
}: {
  readonly addressKey: string | null;
  readonly activeProfileProxyId: string | null;
}): string => JSON.stringify([addressKey, activeProfileProxyId]);
export const getWaveReadProxyRoleIdentityKey = ({
  addressKey,
  proxyCreatorId
}: {
  readonly addressKey: string;
  readonly proxyCreatorId: string;
}): string => JSON.stringify([addressKey, "proxy-role", proxyCreatorId]);
export const getWaveReadRequestKey = ({
  addressKey,
  activeProfileProxyId,
  waveId
}: {
  readonly addressKey: string | null;
  readonly activeProfileProxyId: string | null;
  readonly waveId: string;
}): string => JSON.stringify([addressKey, activeProfileProxyId, waveId]);
export const getWaveReadProxyRoleRequestKey = ({
  addressKey,
  proxyCreatorId,
  waveId
}: {
  readonly addressKey: string;
  readonly proxyCreatorId: string;
  readonly waveId: string;
}): string => JSON.stringify([addressKey, "proxy-role", proxyCreatorId, waveId]);
const getAuthHeaders = (walletAuth: string): AuthHeaders => ({
  Authorization: `Bearer ${walletAuth}`
});
export const isWaveReadJwtExpired = (jwtExpiresAt: number): boolean => jwtExpiresAt <= Math.floor(Date.now() / 1000);
const decodeWaveReadJwtIdentity = (walletAuth: string | null): WaveReadJwtIdentity | undefined => {
  if (!walletAuth) {
    return undefined;
  }
  try {
    const decodedJwt = jwtDecode<WaveReadJwtPayload>(walletAuth);
    const expiresAt = decodedJwt.exp;
    if (typeof expiresAt !== "number" || !Number.isFinite(expiresAt) || isWaveReadJwtExpired(expiresAt)) {
      return undefined;
    }
    const addressKey = decodedJwt.sub?.toLowerCase() ?? null;
    if (!addressKey) {
      return undefined;
    }
    return {
      addressKey,
      proxyCreatorId: typeof decodedJwt.role === "string" && decodedJwt.role.length > 0 ? decodedJwt.role : null,
      jwtExpiresAt: expiresAt
    };
  } catch {
    return undefined;
  }
};
const getVerifiedAuthHeaders = ({
  walletAuth,
  addressKey,
  jwtIdentity,
  activeProfileProxyCreatorId
}: {
  readonly walletAuth: string | null;
  readonly addressKey: string | null;
  readonly jwtIdentity: WaveReadJwtIdentity | undefined;
  readonly activeProfileProxyCreatorId: string | null;
}): AuthHeaders | undefined => {
  if (!walletAuth || !addressKey || !jwtIdentity) {
    return undefined;
  }
  if (jwtIdentity.addressKey !== addressKey) {
    return undefined;
  }
  if (jwtIdentity.proxyCreatorId !== activeProfileProxyCreatorId) {
    return undefined;
  }
  return getAuthHeaders(walletAuth);
};
const getTemporaryProxyRoleIdentity = ({
  addressKey,
  activeProfileProxyId,
  jwtIdentity
}: {
  readonly addressKey: string | null;
  readonly activeProfileProxyId: string | null;
  readonly jwtIdentity: WaveReadJwtIdentity | undefined;
}): WaveReadTemporaryProxyRoleIdentity | undefined => {
  if (addressKey === null || activeProfileProxyId !== null) {
    return undefined;
  }
  const proxyCreatorId = jwtIdentity?.addressKey === addressKey ? jwtIdentity.proxyCreatorId : null;
  if (proxyCreatorId === null) {
    return undefined;
  }
  return {
    addressKey,
    proxyCreatorId,
    identityKey: getWaveReadProxyRoleIdentityKey({
      addressKey,
      proxyCreatorId
    })
  };
};
const getVerifiedIdentity = ({
  addressKey,
  activeProfileProxyId,
  activeProfileProxyCreatorId,
  identityKey,
  jwtIdentity,
  verifiedAuthHeaders
}: {
  readonly addressKey: string | null;
  readonly activeProfileProxyId: string | null;
  readonly activeProfileProxyCreatorId: string | null;
  readonly identityKey: string;
  readonly jwtIdentity: WaveReadJwtIdentity | undefined;
  readonly verifiedAuthHeaders: AuthHeaders | undefined;
}): WaveReadVerifiedIdentity | undefined => {
  if (!verifiedAuthHeaders || addressKey === null || !jwtIdentity) {
    return undefined;
  }
  if (isWaveReadJwtExpired(jwtIdentity.jwtExpiresAt)) {
    return undefined;
  }
  return {
    addressKey,
    activeProfileProxyId,
    activeProfileProxyCreatorId,
    identityKey,
    jwtExpiresAt: jwtIdentity.jwtExpiresAt,
    authHeaders: verifiedAuthHeaders
  };
};
const getProxyRoleIdentityKey = ({
  addressKey,
  activeProfileProxyCreatorId,
  temporaryProxyRoleIdentity
}: {
  readonly addressKey: string | null;
  readonly activeProfileProxyCreatorId: string | null;
  readonly temporaryProxyRoleIdentity: WaveReadTemporaryProxyRoleIdentity | undefined;
}): string | null => {
  if (temporaryProxyRoleIdentity) {
    return temporaryProxyRoleIdentity.identityKey;
  }
  if (addressKey === null || activeProfileProxyCreatorId === null) {
    return null;
  }
  return getWaveReadProxyRoleIdentityKey({
    addressKey,
    proxyCreatorId: activeProfileProxyCreatorId
  });
};
export const useWaveReadIdentityState = t0 => {
  const $ = _c(35);
  const {
    address,
    activeProfileProxyId,
    activeProfileProxyCreatorId,
    walletAuth
  } = t0;
  let t1;
  if ($[0] !== address) {
    t1 = getAddressKey(address);
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const addressKey = t1;
  let t2;
  if ($[2] !== walletAuth) {
    t2 = decodeWaveReadJwtIdentity(walletAuth);
    $[2] = walletAuth;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const jwtIdentity = t2;
  let t3;
  if ($[4] !== activeProfileProxyCreatorId || $[5] !== addressKey || $[6] !== jwtIdentity || $[7] !== walletAuth) {
    t3 = getVerifiedAuthHeaders({
      walletAuth,
      addressKey,
      jwtIdentity,
      activeProfileProxyCreatorId
    });
    $[4] = activeProfileProxyCreatorId;
    $[5] = addressKey;
    $[6] = jwtIdentity;
    $[7] = walletAuth;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const verifiedAuthHeaders = t3;
  let t4;
  if ($[9] !== activeProfileProxyId || $[10] !== addressKey || $[11] !== jwtIdentity) {
    t4 = getTemporaryProxyRoleIdentity({
      addressKey,
      activeProfileProxyId,
      jwtIdentity
    });
    $[9] = activeProfileProxyId;
    $[10] = addressKey;
    $[11] = jwtIdentity;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const temporaryProxyRoleIdentity = t4;
  let t5;
  if ($[13] !== activeProfileProxyId || $[14] !== addressKey || $[15] !== temporaryProxyRoleIdentity?.identityKey) {
    t5 = temporaryProxyRoleIdentity?.identityKey ?? getWaveReadIdentityKey({
      addressKey,
      activeProfileProxyId
    });
    $[13] = activeProfileProxyId;
    $[14] = addressKey;
    $[15] = temporaryProxyRoleIdentity?.identityKey;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const identityKey = t5;
  let t6;
  if ($[17] !== activeProfileProxyCreatorId || $[18] !== addressKey || $[19] !== temporaryProxyRoleIdentity) {
    t6 = getProxyRoleIdentityKey({
      addressKey,
      activeProfileProxyCreatorId,
      temporaryProxyRoleIdentity
    });
    $[17] = activeProfileProxyCreatorId;
    $[18] = addressKey;
    $[19] = temporaryProxyRoleIdentity;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const proxyRoleIdentityKey = t6;
  let t7;
  if ($[21] !== activeProfileProxyCreatorId || $[22] !== activeProfileProxyId || $[23] !== addressKey || $[24] !== identityKey || $[25] !== jwtIdentity || $[26] !== verifiedAuthHeaders) {
    t7 = getVerifiedIdentity({
      addressKey,
      activeProfileProxyId,
      activeProfileProxyCreatorId,
      identityKey,
      jwtIdentity,
      verifiedAuthHeaders
    });
    $[21] = activeProfileProxyCreatorId;
    $[22] = activeProfileProxyId;
    $[23] = addressKey;
    $[24] = identityKey;
    $[25] = jwtIdentity;
    $[26] = verifiedAuthHeaders;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const verifiedIdentity = t7;
  let t8;
  if ($[28] !== activeProfileProxyId || $[29] !== addressKey || $[30] !== identityKey || $[31] !== proxyRoleIdentityKey || $[32] !== temporaryProxyRoleIdentity || $[33] !== verifiedIdentity) {
    t8 = {
      addressKey,
      activeProfileProxyId,
      identityKey,
      proxyRoleIdentityKey,
      temporaryProxyRoleIdentity,
      verifiedIdentity
    };
    $[28] = activeProfileProxyId;
    $[29] = addressKey;
    $[30] = identityKey;
    $[31] = proxyRoleIdentityKey;
    $[32] = temporaryProxyRoleIdentity;
    $[33] = verifiedIdentity;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  return t8;
};